import React, { useState } from "react";
import { Typography, Chip, Avatar } from "@mui/material";
import CustomTable from "../../components/Table/Table.js";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAllAccountsViewModel } from "../manageAccount/AllAccounts/AllAccountsViewModel.js";

import AddSkillCategory from "./AddSkillCategory.js";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useLoading } from "../../contexts/LoadingContext";
import Modal from "../../components/Modal/Modal";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import BusinessIcon from "@mui/icons-material/Business";
import { SkillCategoryModel } from "./SkillCategoryModel.js";
import { useRbac } from "../../hooks/RbacContext.js";

const SkillCategoryLanding = () => {
  const {
    categories,
    loading,
    error,
    handleDelete,
    createSkillCategory,
    UpdateSkillCategory,
    refetch
  } = SkillCategoryModel();

  const { showLoading, hideLoading } = useLoading();

  const [isAddSkillCategoryOpen, setIsAddSkillCategoryOpen] = useState(false);
  const [selectedSkillCategory, setSelectedSkillCategory] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const {checkAccess} = useRbac();

  const columns = [
    {
      id: "skill_cat_name",
      label: "Category Name",
    },
    {
      id: "created_on",
      label: "Created On",
      align: "left",
      render: (value) => {
        if (!value) return '-';
        const [day, month, year] = value.split('/');
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        });
      }
    },
    {
      id: "updated_on",
      label: "Updated On",
      align: "left",
      render: (value) => {
        if (!value) return '-';
        const [day, month, year] = value.split('/');
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        });
      }
    },
  ];
  const fixedColumns = {
    left: [],
    right: ["actions"],
  };
  const handleAddSkillCategory = () => {
    setSelectedSkillCategory(null);
    setIsAddSkillCategoryOpen(true);
  };

  const handleEditSkillCategory = (category) => {
    console.log("The category in Edit icon is ", category)
    setSelectedSkillCategory(category);
    setIsAddSkillCategoryOpen(true);
  };

  const handleCloseDialog = () => {
    setIsAddSkillCategoryOpen(false);
    setSelectedSkillCategory(null);
  };

  const handleSubmit = async (formData) => {
    try {
      showLoading();
      if (selectedSkillCategory) {
        await UpdateSkillCategory(selectedSkillCategory.skill_category_id, formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Skill category updated successfully",
        });
      } else {
        await createSkillCategory(formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Skill category added successfully",
        });
      }
      handleCloseDialog();

    } catch (error) {
      throw error;
    } finally {
      hideLoading();
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModal(null);
  };

  const handleDeleteClick = (category) => {

    setDeleteConfirmation({
      category,
      open: true,
    });
  };

  const handleDeleteConfirm = async () => {
    const category = deleteConfirmation.category;
    try {
      showLoading();
      await handleDelete(category);
      setDeleteConfirmation(null);
      setSuccessModal({
        type: "success",
        title: "Success",
        message: "Skill category deleted successfully",
      });
    } catch (error) {
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: error.message || "Failed to delete skill category",
      });
    } finally {
      hideLoading();
    }
  };

  const getActions = (client) => {

    const actions = [];
    if (checkAccess("skillCategory", "update")) {
      actions.push({
        icon: <EditIcon fontSize="small" />,
        label: "Edit",
        onClick: handleEditSkillCategory,
        color: "primary",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
          },
        },
      });
    }
    if (checkAccess("skillCategory", "delete")) {
      actions.push({
        icon: <DeleteIcon fontSize="small" />,
        label: "Delete",
        onClick: handleDeleteClick,
        color: "error",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.error.lighter || "rgba(211, 47, 47, 0.08)",
          },
        },
      });
    }

    return actions;
  };


  if (error) {
    return (
      <div>
        <Typography color="error">Error: {error}</Typography>
      </div>
    );
  }
 

  return (
    <div>
      <CustomTable
        columns={columns}
        data={categories}
        onAdd={handleAddSkillCategory}
        showActions={true}
        showAddButton={checkAccess("skillCategory", "add")}
        addButtonIcon={<PersonAddIcon />}
        showSearch={true}
        showColumnVisibility={false}
        pageSize={10}
        stickyHeader={true}
        fixedColumns={fixedColumns}
        actions={getActions}
        title="All Skill categories"
        subtitle="Manage and monitor all skill category"
        addButtonText="Add Skill Category"
        alignment="left"
        fontSize="12px"
        showFilters={true}
      />

      <AddSkillCategory
        open={isAddSkillCategoryOpen}
        onClose={handleCloseDialog}
        initialData={selectedSkillCategory}
        onSubmit={handleSubmit}
      />

      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={handleCloseSuccessModal}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationModal
          open={true}
          title="Delete Skill Category"
          message={`Are you sure you want to delete skill category "${deleteConfirmation.category.skill_cat_name}"?`}
          onClose={() => setDeleteConfirmation(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default SkillCategoryLanding;
